// src/components/forms/ReservationSuccessModal.tsx
import { motion } from 'framer-motion';
import { CheckCircle, X, Mail, Clock, PartyPopper } from 'lucide-react';

interface FormStatus {
  type: 'success' | 'error' | null;
  message: string;
  isSubmitting: boolean;
}

interface ReservationSuccessModalProps {
  isVisible: boolean;
  onClose: () => void;
  formStatus: FormStatus;
  translations: {
    home: {
      reservation: {
        form: {
          button: string;
        };
      };
    };
  };
}

const ReservationSuccessModal = ({
  isVisible,
  onClose,
  formStatus,
  translations: t,
}: ReservationSuccessModalProps) => {
  if (!isVisible) return null;

  const isEn = t.home.reservation.form.button === 'Request Reservation';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 24, stiffness: 300, duration: 0.35 }}
        className="rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden bg-white"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — darker green gradient (hardcoded) */}
        <div className="relative px-6 py-8 text-center bg-gradient-to-r from-[#2E7D32] to-[#1B5E20]">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/85 hover:text-white transition-colors p-2 rounded-full hover:bg-white/15"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, type: 'spring', damping: 14, stiffness: 260 }}
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.35 }}
            className="text-white text-2xl font-serif font-bold tracking-wide"
          >
            {isEn ? 'Reservation Request Sent!' : 'Bókunarbeiðni send!'}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.33, duration: 0.35 }}
            className="text-white/95 text-sm"
          >
            {isEn ? "We'll get back to you soon" : 'Við munum hafa samband fljótlega'}
          </motion.p>
        </div>

        {/* Content — stronger contrast (hardcoded colors) */}
        <div className="px-6 py-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.35 }}
            className="text-center space-y-4"
          >
            <div
              className="rounded-lg p-4 border"
              style={{ backgroundColor: '#E8F5E9', borderColor: '#C8E6C9' }}
            >
              <p className="text-sm leading-relaxed" style={{ color: '#1F2937' /* slate-800 */ }}>
                {formStatus.message}
              </p>
            </div>

            <div className="rounded-lg p-4" style={{ backgroundColor: '#F8FAFC' /* slate-50 */ }}>
              <div
                className="flex items-center gap-2 mb-3"
                style={{ color: '#374151' /* gray-700 */ }}
              >
                <Mail className="w-4 h-4" style={{ color: '#B91C1C' }} />
                <span className="text-sm font-medium">
                  {isEn
                    ? 'Check your email for confirmation'
                    : 'Athugaðu tölvupóstinn þinn fyrir staðfestingu'}
                </span>
              </div>

              <div className="flex items-center gap-2 mb-3" style={{ color: '#374151' }}>
                <Clock className="w-4 h-4" style={{ color: '#B91C1C' }} />
                <span className="text-sm">
                  {isEn ? 'Response within 24 hours' : 'Svar innan 24 klukkustunda'}
                </span>
              </div>

              <div className="flex items-center gap-2" style={{ color: '#374151' }}>
                <PartyPopper className="w-4 h-4" style={{ color: '#B91C1C' }} />
                <span className="text-sm">
                  {isEn ? "We're excited to host you!" : 'Við hlökkum til að taka á móti þér!'}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer — extra cool buttons */}
        <div className="px-6 pb-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.48, duration: 0.35 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            {/* Cool dark glossy button */}
            <button
              onClick={onClose}
              className="
                relative group flex-1 inline-flex items-center justify-center rounded-xl
                px-6 py-3 font-medium tracking-wide
                transition-all duration-300
                shadow-[0_8px_20px_rgba(0,0,0,0.18)]
                hover:-translate-y-0.5 active:translate-y-0
                border border-white/10
              "
              style={{
                background: 'linear-gradient(180deg, #1F2937 0%, #0F172A 100%)',
                color: '#FFFFFF',
              }}
            >
              <span
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 60%)',
                }}
              />
              <span className="relative">{isEn ? 'Close' : 'Loka'}</span>
            </button>

            {/* Gradient border button (brand red) */}
            <button
              onClick={() => {
                onClose();
                window.location.href = '/menu';
              }}
              className="
                flex-1 rounded-xl p-[2px] transition-transform duration-300 hover:-translate-y-0.5
                shadow-[0_8px_20px_rgba(185,28,28,0.25)]
              "
              style={{
                background: 'linear-gradient(90deg, #EF4444 0%, #DC2626 50%, #B91C1C 100%)',
              }}
            >
              <span
                className="block w-full h-full rounded-[10px] px-6 py-3 text-center font-medium"
                style={{
                  background: 'white',
                  color: '#B91C1C',
                }}
              >
                {isEn ? 'View Menu' : 'Skoða matseðil'}
              </span>
            </button>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ReservationSuccessModal;
